// Video testimonials (sections/video-testimonials.liquid): each card's video previews muted and looping while
// it's on screen (never with reduced motion: the poster and play button only); the play button plays the same
// video from the start with sound and its controls, one at a time, until it ends or leaves the screen. One at a
// time across the page too: a video starting with sound announces itself ("stay:sound-play" on document) and
// every other player (these cards, the purchase module's UGC clips) stops its own. Moves
// the row with the arrow buttons or by dragging with a mouse (touch screens swipe natively). Safe to load twice.
if (!customElements.get('video-testimonials')) {
  class VideoTestimonials extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.video-testimonials__list');
      if (!this.list) return;

      this.onClick = this.onClick.bind(this);
      this.list.addEventListener('click', this.onClick);
      this.setUpVideos();

      this.arrows = this.querySelector('.video-testimonials__arrows');
      if (this.arrows) {
        this.previousButton = this.arrows.querySelector('[name="previous"]');
        this.nextButton = this.arrows.querySelector('[name="next"]');
        this.previousButton.addEventListener('click', () => this.move(-1));
        this.nextButton.addEventListener('click', () => this.move(1));
      }

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.list);
      this.update();

      this.setUpDrag();

      // Theme editor: bring the selected video into view
      this.onBlockSelect = (event) => {
        if (this.contains(event.target)) this.scrollToCard(event.target);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.reduced?.removeEventListener('change', this.refreshVideos);
      document.removeEventListener('stay:sound-play', this.onSoundPlay);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onClick(event) {
      const button = event.target.closest('.video-testimonials__play');
      if (!button) return;

      const video = button.closest('.video-testimonials__media').querySelector('video');
      if (video) this.playWithSound(video);
    }

    setUpVideos() {
      this.videos = [...this.list.querySelectorAll('.video-testimonials__video')];
      if (!this.videos.length) return;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.refreshVideos = this.refreshVideos.bind(this);
      this.visible = new Set();
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.visible.add(entry.target);
          } else {
            this.visible.delete(entry.target);
            if (this.hasSound(entry.target)) this.stopSound(entry.target);
          }
        });
        this.refreshVideos();
      });
      this.videos.forEach((video) => {
        this.videoObserver.observe(video);
        video.addEventListener('ended', () => {
          this.stopSound(video);
          this.refreshVideos();
        });
      });
      this.reduced.addEventListener('change', this.refreshVideos);

      // One video with sound on the page: announce ours, stop ours when another one starts
      this.videos.forEach((video) =>
        video.addEventListener('play', () => {
          if (!video.muted) document.dispatchEvent(new CustomEvent('stay:sound-play', { detail: { video } }));
        })
      );
      this.onSoundPlay = (event) => {
        const stopped = this.videos.filter((video) => video !== event.detail.video && this.hasSound(video));
        stopped.forEach((video) => this.stopSound(video));
        if (stopped.length) this.refreshVideos();
      };
      document.addEventListener('stay:sound-play', this.onSoundPlay);
    }

    hasSound(video) {
      return video.closest('.video-testimonials__media').classList.contains('is-playing');
    }

    // Previews: muted and looping while on screen, paused off it; the poster stays until a video actually plays
    refreshVideos() {
      this.videos.forEach((video) => {
        if (this.hasSound(video)) return;

        if (!this.reduced.matches && this.visible.has(video)) {
          video.muted = true;
          video
            .play()
            .then(() => video.classList.add('is-previewing'))
            .catch(() => {});
        } else {
          if (!video.paused) video.pause();
          if (this.reduced.matches) video.classList.remove('is-previewing');
        }
      });
    }

    playWithSound(video) {
      this.videos.forEach((other) => {
        if (other !== video && this.hasSound(other)) this.stopSound(other);
      });
      this.refreshVideos();

      video.closest('.video-testimonials__media').classList.add('is-playing');
      video.removeAttribute('aria-hidden');
      video.controls = true;
      video.loop = false;
      video.muted = false;
      video.currentTime = 0;
      video.play().catch(() => {
        // Playback was blocked; the video's own controls are showing, so it can be started from there
      });
      // The play button is hidden now, so keyboard focus moves to the video and its controls
      video.focus();
    }

    // Back to the preview (the caller refreshes it); focus inside the video returns to the play button
    stopSound(video) {
      const media = video.closest('.video-testimonials__media');
      const hadFocus = media.contains(document.activeElement);

      video.pause();
      video.muted = true;
      video.loop = true;
      video.controls = false;
      video.setAttribute('aria-hidden', 'true');
      media.classList.remove('is-playing');
      if (hadFocus) media.querySelector('.video-testimonials__play').focus({ preventScroll: true });
    }

    // Mouse drag. Scroll snap is switched off while dragging (it would fight the pointer) and the row then
    // settles on the nearest card. A drag of more than a few pixels isn't a click, so it doesn't start a
    // video or follow the handle link.
    setUpDrag() {
      this.list.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.overflows) return;
        // Leave the playing video's own controls (seek bar, volume) alone
        if (event.target.closest('video')) return;

        this.drag = { x: event.clientX, scrollLeft: this.list.scrollLeft, pointerId: event.pointerId, moved: false };
      });

      this.list.addEventListener('pointermove', (event) => {
        if (!this.drag || event.pointerId !== this.drag.pointerId) return;

        const distance = event.clientX - this.drag.x;
        if (!this.drag.moved) {
          if (Math.abs(distance) < 5) return;
          this.drag.moved = true;
          this.list.setPointerCapture(event.pointerId);
          this.list.classList.add('is-dragging');
        }
        this.list.scrollLeft = this.drag.scrollLeft - distance;
      });

      const endDrag = (event) => {
        if (!this.drag || event.pointerId !== this.drag.pointerId) return;

        const { moved } = this.drag;
        this.drag = null;
        if (!moved) return;

        this.list.classList.remove('is-dragging');
        this.snapToNearestCard();

        // The click that follows the drag (same task as pointerup) is swallowed; if the pointer was released
        // outside the row no click comes, so the flag is cleared on the next tick
        this.suppressClick = true;
        setTimeout(() => (this.suppressClick = false), 0);
      };
      this.list.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true }
      );
      this.list.addEventListener('pointerup', endDrag);
      this.list.addEventListener('pointercancel', endDrag);

      // Stops the browser dragging the preview image or the handle link instead of the row
      this.list.addEventListener('dragstart', (event) => event.preventDefault());
    }

    snapToNearestCard() {
      const listLeft = this.list.getBoundingClientRect().left + this.inset;
      const cards = [...this.list.querySelectorAll('.video-testimonials__card')];
      if (!cards.length) return;

      const nearest = cards.reduce((best, card) =>
        Math.abs(card.getBoundingClientRect().left - listLeft) < Math.abs(best.getBoundingClientRect().left - listLeft)
          ? card
          : best
      );
      this.scrollToCard(nearest);
    }

    get reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get inset() {
      return parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
    }

    // One "page" of whole cards at a time; scroll snap lines the next card up with the row start
    move(direction) {
      const card = this.list.querySelector('.video-testimonials__card');
      if (!card) return;

      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      const step = card.offsetWidth + gap;
      const cards = Math.max(1, Math.floor((this.list.clientWidth - 2 * this.inset + gap) / step));
      this.list.scrollBy({ left: direction * step * cards, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    scrollToCard(card) {
      const offset = card.getBoundingClientRect().left - this.list.getBoundingClientRect().left;
      this.list.scrollTo({
        left: this.list.scrollLeft + offset - this.inset,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
    }

    update() {
      const max = this.list.scrollWidth - this.list.clientWidth;
      this.overflows = max > 1;
      this.list.classList.toggle('is-draggable', this.overflows);

      if (!this.arrows) return;
      this.arrows.hidden = !this.overflows;
      this.previousButton.disabled = this.list.scrollLeft <= 1;
      this.nextButton.disabled = this.list.scrollLeft >= max - 1;
    }
  }

  customElements.define('video-testimonials', VideoTestimonials);
}
